import React from 'react';
import { Product } from '../types';
import { useShop } from '../context/ShopContext';
import { ShoppingBag, Eye, Tag, Sparkles, Check, Flame } from 'lucide-react';
import { SchoolBadge } from './SchoolBadge';
import { getTotalStock } from '../services/inventoryService';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { schools, reservations, settings, openProductDetail } = useShop();

  const school = schools.find((s) => s.id === product.schoolId);

  const totalStock = getTotalStock(product);

  const isLowStock = totalStock > 0 && totalStock <= 3;
  const isOutOfStock = totalStock <= 0;

  // Dynamic Popular Label calculation based on live Firestore reservation data
  const reservedCount = reservations
    .filter((r) => r.status !== 'Cancelled' && r.items)
    .reduce((acc, r) => {
      const match = r.items.find((i) => i.productId === product.id);
      return acc + (match ? match.quantity || 1 : 0);
    }, 0);

  const threshold = settings.popularThreshold ?? 10;
  const isPopular = reservedCount > 0 && reservedCount >= threshold;

  return (
    <div
      onClick={() => openProductDetail(product)}
      className="group relative bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-blue-300/60 hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
    >
      
      {/* Top Image & Ribbon Container */}
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={product.images[0] || 'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&w=600&q=80'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          decoding="async"
        />

        {/* Badges Stack */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10 max-w-[85%]">
          {product.recentlyAdded && (
            <span className="inline-flex items-center gap-1 bg-[#002366] text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
              <Sparkles className="w-3 h-3 text-sky-300" />
              Recently Added
            </span>
          )}

          {product.isNewDonation && (
            <span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
              New Stock
            </span>
          )}

          {isPopular && (
            <span className="inline-flex items-center gap-1 bg-purple-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
              <Flame className="w-3 h-3 text-amber-300" />
              Popular ({reservedCount} reserved)
            </span>
          )}

          {isOutOfStock && (
            <span className="inline-flex items-center gap-1 bg-rose-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
              Out of Stock
            </span>
          )}

          {isLowStock && (
            <span className="inline-flex items-center gap-1 bg-amber-400 text-slate-950 text-[10px] font-extrabold px-2.5 py-1 rounded-full shadow-md">
              Low Stock ({totalStock} left)
            </span>
          )}
        </div>

        {/* Hover Quick View overlay button */}
        <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <span className="bg-white/95 backdrop-blur-md text-[#002366] text-xs font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Select Size & View
          </span>
        </div>
      </div>

      {/* Card Content Body */}
      <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
        <div>
          {/* School Badge Pill */}
          <div className="mb-2">
            <SchoolBadge school={school} schoolName={school?.name || 'Immingham School'} size="sm" />
          </div>

          {/* Product Title */}
          <h3 className="font-heading font-bold text-slate-900 text-base leading-snug line-clamp-2 hover:text-blue-600 transition">
            {product.name}
          </h3>

          {/* Colour & Gender */}
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-600 font-medium">
            {product.color && (
              <span className="bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200">
                Colour: <strong className="text-slate-900">{product.color}</strong>
              </span>
            )}
            <span className="text-slate-500 text-[11px] font-semibold bg-slate-100 px-2 py-0.5 rounded-full">
              {product.gender || 'Unisex'}
            </span>
          </div>

          {/* Available Sizes Pills */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Available Sizes
              </div>
              <div className="flex flex-wrap gap-1">
                {product.sizes.map((s, idx) => (
                  <span
                    key={s.id || idx}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${
                      s.stock > 0
                        ? 'bg-blue-50/80 text-blue-900 border-blue-200'
                        : 'bg-slate-100 text-slate-400 border-slate-200 line-through opacity-60'
                    }`}
                  >
                    {s.size} ({s.stock})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Price & Action Row */}
        <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1 text-emerald-600 font-extrabold text-sm">
              <span>100% FREE</span>
            </div>
            <div className="text-[11px] font-semibold">
              {isOutOfStock ? (
                <span className="text-rose-600">Out of stock</span>
              ) : (
                <span className="text-emerald-700">{totalStock} Total in Stock</span>
              )}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              openProductDetail(product);
            }}
            disabled={isOutOfStock}
            className={`px-4 py-2.5 rounded-full text-xs font-heading font-bold flex items-center gap-1.5 transition-all ${
              isOutOfStock
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 via-blue-700 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.97]'
            }`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>{isOutOfStock ? 'Out of Stock' : 'Select Size'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};
